/**
 * Help search — a real full-text index over the help corpus.
 *
 * The whole corpus is typed data that ships in the bundle, so there is nothing
 * to fetch: the index is built once at module load, in a few milliseconds, over
 * a couple of hundred short documents.
 *
 * WHAT A DOCUMENT IS. One document per thing a reader can land ON, not per
 * page — so a hit deep-links to the heading that answers the question rather
 * than dumping the reader at the top of a long topic:
 *   - the TOPIC itself (its title, summary and intro),
 *   - each RECIPE (the task line, its steps, its note),
 *   - each SECTION (heading + body),
 *   - each GLOSSARY entry, which is section-shaped (term + definition). Without
 *     it the glossary — the densest page of vocabulary in the app — would only
 *     ever match the word "glossary".
 * Recipes, sections and glossary terms all render as anchored headings, so
 * every non-topic document has a fragment to link to. Related links are
 * navigation, not content, and are not indexed.
 *
 * THE INDEX DERIVES ITSELF. `buildHelpIndex` walks whatever registry it is
 * handed. Adding or editing a topic makes it searchable with no curation step —
 * there is no per-topic keyword list to forget to update, and a test pins that
 * by indexing a topic the registry has never seen.
 *
 * FIELDS AND BOOSTS. `title` (the topic's) outranks `heading` (the document's
 * own), which outranks `aliases`, which outranks `body`. `aliases` is the
 * domain map read backwards — the words a section MEANS without saying them,
 * so "money" reaches a section about totals. See `helpSynonyms.ts`.
 *
 * MATCHING IS FORGIVING ON PURPOSE. Prefix matching ("rollup" finds "rollups"),
 * fuzzy matching for typos, and OR rather than AND so a half-remembered phrase
 * still lands. The reader is a PM who types "money looks wrong", not a query
 * language. Precision comes back through ranking: exact beats prefix beats
 * fuzzy, literal query words beat words the synonym map added, and results
 * scoring far below the leader are cut.
 */
import MiniSearch, { type SearchResult } from 'minisearch';
import { HELP_ORDER, HELP_TOPICS } from '../help/topics';
import { headingId } from '../help/anchors';
import type { HelpSlug, HelpTopicDef } from '../help/types';
import { aliasesForText, expandTerms, HELP_STOP_WORDS } from './helpSynonyms';

export type HelpSearchKind = 'topic' | 'recipe' | 'section';

export interface HelpSearchResult {
  topicSlug: HelpSlug;
  /** Topic title — rendered as the eyebrow above the label. */
  topicTitle: string;
  kind: HelpSearchKind;
  /** The topic title, the recipe's task, or the section heading / glossary term. */
  label: string;
  /** One line of match context, pulled from the sentence that matched. */
  snippet: string;
  /** Fragment id on the topic page. Absent on topic units (they are the page). */
  anchor?: string;
  /** Document words this result matched on — what the UI highlights. */
  terms: string[];
  /** Relevance. Exposed so callers can reason about ordering; not for display. */
  score: number;
}

/** Results returned to the UI. Past this, narrowing the query beats scrolling. */
export const HELP_SEARCH_LIMIT = 12;

/** One topic must not be able to fill the whole list with its own sections. */
const MAX_PER_TOPIC = 3;

const SNIPPET_MAX = 150;

/**
 * A hit through the synonym map counts, but never as loudly as the real word.
 * Applied per query term, so a result that matched the typed word always
 * outranks one that only matched what the word was expanded to.
 */
const SYNONYM_FACTOR = 0.45;

/** The topic page beats one of its own sections when both match equally well. */
const TOPIC_BOOST = 1.25;

/**
 * A heading typed VERBATIM sorts above anything that merely mentions it.
 *
 * BM25 cannot promise that on its own. A document that IS the query
 * ("Materials", the section in how-the-math-works) scored a hair UNDER one that
 * merely contains the word among several ("Copy the materials list") — 47.91 vs
 * 47.93 in the live corpus — and which side of a result limit a gap that size
 * falls on moves every time the corpus grows by one document. Writing any new
 * help entry anywhere could therefore push an unrelated heading out of its own
 * results: a bad search result, and a booby trap for the next person who
 * authors a topic.
 *
 * Deliberately a RE-RANK and not a score multiplier. A multiplier would inflate
 * the leader's score, and the relevance floor is a share OF the leader — so
 * boosting the exact hit would silently cut every weaker-but-legitimate result
 * off the end of the list. Which documents survive is unchanged; only their
 * order is.
 */
function exactHeadingRank(headingKey: string | undefined, queryKey: string): number {
  return queryKey !== '' && headingKey === queryKey ? 1 : 0;
}

/**
 * Everything scoring below this share of the leader is noise the reader would
 * have to scroll past. With OR matching, one common word in a long query drags
 * in documents that have nothing to do with the question.
 */
const SCORE_FLOOR_RATIO = 0.18;

const FIELD_BOOST = { title: 4, heading: 3, aliases: 2, body: 1 };

/** Prefix and fuzzy hits are real, but an exact word is always better. */
const MATCH_WEIGHTS = { prefix: 0.45, fuzzy: 0.2 };

/**
 * Typo tolerance, for words the reader actually typed only. Below five
 * characters it is indistinguishable from a different word — one edit turns
 * "cant" into "want" and "late" into "date", and those are common enough in
 * this corpus to bury the real answer. Two edits is the ceiling regardless of
 * length; that is what reaches "received" from "recieved", where a
 * transposition costs two.
 *
 * Words the synonym map ADDED never fuzzy-match: the expansion is already an
 * inference, and letting it drift another edit turns "login → link" into
 * "line", which matches every labor row in the corpus.
 */
const FUZZY_RATIO = 0.2;
const FUZZY_MIN_LENGTH = 5;
const MAX_FUZZY = 2;

/** One and two-letter terms ("t", "m" from "T&M") would prefix half the corpus. */
const PREFIX = (term: string) => term.length >= 3;

// ── Normalization ──────────────────────────────────────────────────────────
// Lowercase, drop apostrophes so "sub's" reads as one word ("subs"), and turn
// every other run of punctuation into a single space. "Approve & Send" and
// "approve and send"… the ampersand simply disappears from both sides, which
// is what makes "approve send" find it.

export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[’‘'`´]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function tokenize(text: string): string[] {
  const n = normalize(text);
  return n === '' ? [] : n.split(' ');
}

// ── Documents ──────────────────────────────────────────────────────────────

interface Unit {
  id: string;
  topicSlug: HelpSlug;
  topicTitle: string;
  kind: HelpSearchKind;
  label: string;
  anchor?: string;
  /** Topic title. Every unit of a topic carries it — that is the point. */
  title: string;
  /** This unit's own name: section heading, recipe task, or glossary term. */
  heading: string;
  /** Words this unit means but does not say. Derived, never authored. */
  aliases: string;
  /** This unit's prose. */
  body: string;
  /** Un-normalized prose, one entry per paragraph/step, for snippets. */
  rawParts: string[];
  /** Registry order, then authored order — the tie-break. */
  rank: number;
}

function unitAliases(unit: Omit<Unit, 'aliases'>): string {
  const text = normalize([unit.title, unit.heading, unit.body].join(' '));
  return aliasesForText(text)
    .flatMap((key) => key.split(' '))
    .filter((word) => !HELP_STOP_WORDS.has(word))
    .join(' ');
}

function buildUnits(
  topics: Readonly<Record<string, HelpTopicDef>>,
  order: readonly string[],
): Unit[] {
  const slugs = [
    ...order.filter((s) => topics[s]),
    ...Object.keys(topics).filter((s) => !order.includes(s)),
  ];
  const units: Unit[] = [];
  let rank = 0;

  const push = (unit: Omit<Unit, 'aliases'>) => {
    units.push({ ...unit, aliases: unitAliases(unit) });
  };

  for (const slug of slugs) {
    const topic = topics[slug];
    if (!topic) continue;
    // Production's only caller is the registry, whose keys ARE HelpSlug. Tests
    // hand in ad-hoc registries to prove the builder needs no curation.
    const base = { topicSlug: slug as HelpSlug, topicTitle: topic.title, title: topic.title };

    const introParts = topic.intro ?? [];
    push({
      ...base,
      id: slug,
      kind: 'topic',
      label: topic.title,
      heading: topic.title,
      body: [topic.summary, ...introParts].join(' '),
      rawParts: [...introParts, topic.summary],
      rank: rank++,
    });

    for (const recipe of topic.recipes ?? []) {
      const parts = [...recipe.steps, ...(recipe.note ? [recipe.note] : [])];
      const anchor = headingId(recipe.task);
      push({
        ...base,
        id: `${slug}#${anchor}`,
        kind: 'recipe',
        label: recipe.task,
        anchor,
        heading: recipe.task,
        body: parts.join(' '),
        rawParts: parts,
        rank: rank++,
      });
    }

    for (const section of topic.sections ?? []) {
      const anchor = headingId(section.heading);
      push({
        ...base,
        id: `${slug}#${anchor}`,
        kind: 'section',
        label: section.heading,
        anchor,
        heading: section.heading,
        body: section.body.join(' '),
        rawParts: section.body,
        rank: rank++,
      });
    }

    // A glossary entry is a section with a one-paragraph body.
    for (const entry of topic.glossary ?? []) {
      const anchor = headingId(entry.term);
      push({
        ...base,
        id: `${slug}#${anchor}`,
        kind: 'section',
        label: entry.term,
        anchor,
        heading: entry.term,
        body: entry.def,
        rawParts: [entry.def],
        rank: rank++,
      });
    }
  }

  return units;
}

// ── Snippets ───────────────────────────────────────────────────────────────

/**
 * Split a paragraph or step into sentences. Fragments shorter than a plausible
 * sentence are glued onto the next one, which is what keeps "(e.g." from
 * becoming a snippet of its own.
 */
function sentences(part: string): string[] {
  const raw = part.match(/[^.!?]+[.!?]*/g);
  if (!raw) return [part];
  const out: string[] = [];
  let pending = '';
  for (const fragment of raw) {
    pending += fragment;
    if (pending.trim().length >= 30) {
      out.push(pending);
      pending = '';
    }
  }
  if (pending.trim() !== '') {
    const last = out[out.length - 1];
    if (last === undefined) out.push(pending);
    else out[out.length - 1] = last + pending;
  }
  return out;
}

function clamp(text: string): string {
  const t = text.trim();
  if (t.length <= SNIPPET_MAX) return t;
  const cut = t.slice(0, SNIPPET_MAX);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 60 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/** The sentence covering the most matched words, else the opening sentence. */
function snippetFor(unit: Unit, terms: ReadonlySet<string>): string {
  let best = '';
  let bestHits = 0;
  for (const part of unit.rawParts) {
    for (const sentence of sentences(part)) {
      const words = tokenize(sentence);
      if (words.length === 0) continue;
      const hits = new Set(words.filter((w) => terms.has(w))).size;
      if (best === '') {
        best = sentence;
        bestHits = hits;
      } else if (hits > bestHits) {
        best = sentence;
        bestHits = hits;
      }
    }
  }
  return best === '' ? '' : clamp(best);
}

// ── Highlighting ───────────────────────────────────────────────────────────

export interface SnippetSegment {
  text: string;
  /** True when this run of text is one of the words the result matched on. */
  hit: boolean;
}

/**
 * Split display text into plain and matched runs, for the UI to wrap in
 * `<mark>`. Adjacent matched words are merged with the space between them, so
 * "per diem" highlights as one phrase rather than two.
 */
export function highlightSegments(text: string, terms: readonly string[]): SnippetSegment[] {
  if (text === '') return [];
  const wanted = new Set(terms.map((t) => t.toLowerCase()));
  if (wanted.size === 0) return [{ text, hit: false }];

  const segments: SnippetSegment[] = [];
  const append = (chunk: string, hit: boolean) => {
    if (chunk === '') return;
    const last = segments[segments.length - 1];
    if (last && last.hit === hit) last.text += chunk;
    else segments.push({ text: chunk, hit });
  };

  const wordRe = /[A-Za-z0-9]+/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = wordRe.exec(text)) !== null) {
    const gap = text.slice(cursor, match.index);
    const hit = wanted.has(match[0].toLowerCase());
    const afterHit = segments[segments.length - 1]?.hit === true;
    // A single space between two hits stays inside the highlight.
    append(gap, hit && afterHit && gap === ' ');
    append(match[0], hit);
    cursor = match.index + match[0].length;
  }
  append(text.slice(cursor), false);
  return segments;
}

// ── The index ──────────────────────────────────────────────────────────────

export interface HelpIndex {
  search(query: string, limit?: number): HelpSearchResult[];
  /** Document count — a cheap sanity handle for tests and for reasoning about cost. */
  readonly unitCount: number;
}

/**
 * Build a searchable index from a help registry. Everything is derived: pass a
 * registry with a topic in it and that topic is searchable, full stop.
 */
export function buildHelpIndex(
  topics: Readonly<Record<string, HelpTopicDef>>,
  order: readonly string[] = [],
): HelpIndex {
  const units = buildUnits(topics, order);
  const byId = new Map(units.map((u) => [u.id, u]));
  // Each unit's own name in query form, so an exact match is a lookup rather
  // than a per-hit re-tokenization.
  const headingKeyById = new Map(units.map((u) => [u.id, tokenize(u.heading).join(' ')]));

  const mini = new MiniSearch<Unit>({
    fields: ['title', 'heading', 'aliases', 'body'],
    idField: 'id',
    tokenize,
    // Terms arrive already normalized by `tokenize`; nothing further to do,
    // and stop words stay IN the index so an all-stop-words query can still
    // match something rather than showing a dead end.
    processTerm: (term) => term,
  });
  mini.addAll(units);

  function search(query: string, limit: number = HELP_SEARCH_LIMIT): HelpSearchResult[] {
    const all = tokenize(query);
    if (all.length === 0) return [];
    /** The whole query in the same form a unit's heading reduces to. */
    const queryKey = all.join(' ');

    const meaningful = all.filter((w) => !HELP_STOP_WORDS.has(w));
    const literal = meaningful.length > 0 ? meaningful : all;
    const expanded = expandTerms(all).filter((w) => !literal.includes(w));
    const literalSet = new Set(literal);

    const hits = mini.search(
      { queries: [...literal, ...expanded], combineWith: 'OR' },
      {
        boost: FIELD_BOOST,
        weights: MATCH_WEIGHTS,
        prefix: PREFIX,
        fuzzy: (term) =>
          literalSet.has(term) && term.length >= FUZZY_MIN_LENGTH ? FUZZY_RATIO : false,
        maxFuzzy: MAX_FUZZY,
        boostTerm: (term) => (literalSet.has(term) ? 1 : SYNONYM_FACTOR),
        boostDocument: (id) => (byId.get(String(id))?.kind === 'topic' ? TOPIC_BOOST : 1),
      },
    );
    if (hits.length === 0) return [];

    const floor = (hits[0]?.score ?? 0) * SCORE_FLOOR_RATIO;
    const ranked = hits
      .filter((h: SearchResult) => h.score >= floor)
      .map((h: SearchResult) => ({ hit: h, unit: byId.get(String(h.id)) }))
      .filter((r): r is { hit: SearchResult; unit: Unit } => r.unit !== undefined)
      .sort(
        (a, b) =>
          exactHeadingRank(headingKeyById.get(b.unit.id), queryKey) -
            exactHeadingRank(headingKeyById.get(a.unit.id), queryKey) ||
          b.hit.score - a.hit.score ||
          a.unit.rank - b.unit.rank,
      );

    const perTopic = new Map<HelpSlug, number>();
    const results: HelpSearchResult[] = [];
    for (const { hit, unit } of ranked) {
      if (results.length >= limit) break;
      const used = perTopic.get(unit.topicSlug) ?? 0;
      if (used >= MAX_PER_TOPIC) continue;
      perTopic.set(unit.topicSlug, used + 1);
      const terms = hit.terms.map((t) => t.toLowerCase());
      results.push({
        topicSlug: unit.topicSlug,
        topicTitle: unit.topicTitle,
        kind: unit.kind,
        label: unit.label,
        snippet: snippetFor(unit, new Set(terms)),
        anchor: unit.anchor,
        terms,
        score: hit.score,
      });
    }
    return results;
  }

  return { search, unitCount: units.length };
}

/** Built once, at module load. The corpus is static data in the bundle. */
const INDEX = buildHelpIndex(HELP_TOPICS, HELP_ORDER);

/** Document count — a cheap sanity handle for tests and for reasoning about cost. */
export const HELP_UNIT_COUNT = INDEX.unitCount;

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Search the help corpus. Empty, whitespace-only, or punctuation-only queries
 * return no results — the caller shows its normal topic list instead.
 */
export function searchHelp(query: string, limit: number = HELP_SEARCH_LIMIT): HelpSearchResult[] {
  return INDEX.search(query, limit);
}

/** In-app path for a result, including the fragment when it has one. */
export function helpResultPath(result: HelpSearchResult): string {
  return `/help/${result.topicSlug}${result.anchor ? `#${result.anchor}` : ''}`;
}
